import React from "react";
import {
    Button,
    Grid,
    FormControlLabel,
    Checkbox,
    Box,
    Typography,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { BaseDrawer } from "components/Shared/Base/BaseDrawer";
import LoadingWrapper from "components/Shared/LoadingWrapper";
import InnerShadowBox from "components/Shared/InnerShadowBox";
import { BASE_CONSTANTS } from "constants/baseConstants";
import BaseTextField from "components/Shared/Base/BaseTextField";
import { useAuth } from "hooks";

const ServiceChargeForm = ({
    isBusy = false,
    serviceChargeData = null,
    onSubmit = () => { },
    onCancel = () => { },
    entityId = null,
}) => {
    const { viewEntity } = useAuth();

    const isEditMode = !!serviceChargeData;

    const {
        control,
        handleSubmit,
        reset,
        watch,
        formState: { errors },
    } = useForm({
        defaultValues: {
            serviceAmount: "",
            isActive: false,
        },
    });

    const isActive = watch("isActive");

    // Reset on edit / new
    React.useEffect(() => {
        if (serviceChargeData) {
            reset({
                serviceAmount: serviceChargeData.serviceAmount?.toString() || "",
                isActive: serviceChargeData.isActive ?? true,
            });
        } else {
            reset({
                serviceAmount: "",
                isActive: true,
            });
        }
    }, [serviceChargeData, reset]);

    const handleOnSubmit = (data) => {
        const payload = {
            entityId: entityId || viewEntity?.entity?._id,
            serviceAmount: Number(data.serviceAmount),
            isActive: Boolean(data.isActive),
        };
        onSubmit(payload);
    };

    const handleCancel = () => {
        reset();
        onCancel();
    };

    return (
        <LoadingWrapper loading={isBusy}>
            <InnerShadowBox>
                <BaseDrawer.Content>
                    <Grid container spacing={BASE_CONSTANTS.SPACING_LG}>
                        {/* Active Checkbox */}
                        <Grid item xs={12}>
                            <Controller
                                name="isActive"
                                control={control}
                                render={({ field }) => (
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                {...field}
                                                checked={field.value}
                                                onChange={(e) => field.onChange(e.target.checked)}
                                                color="primary"
                                            />
                                        }
                                        label={
                                            <Box>
                                                <Typography fontWeight={500}>Active Service Charge</Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {isEditMode ? (
                                                        isActive ? "This service charge is active."
                                                            : "This service charge is inactive."
                                                    ) : (
                                                        "This will be set as active by default."
                                                    )}
                                                </Typography>
                                            </Box>
                                        }
                                        sx={{ alignItems: "flex-start", mt: 1 }}
                                    />
                                )}
                            />
                        </Grid>
                        {/* Service Amount Input */}
                        <Grid item xs={12}>
                            <Controller
                                name="serviceAmount"
                                control={control}
                                rules={{
                                    required: "Service amount is required",
                                    min: { value: 0, message: "Amount cannot be negative" },
                                }}
                                render={({ field }) => (
                                    <BaseTextField
                                        {...field}
                                        required
                                        fullWidth
                                        label="Service Charge (Amount)"
                                        type="number"
                                        inputProps={{
                                            min: 0,
                                            step: "0.01",
                                        }}
                                        error={!!errors.serviceAmount}
                                        helperText={errors.serviceAmount?.message}
                                    />
                                )}
                            />
                        </Grid>

                    </Grid>
                </BaseDrawer.Content>
            </InnerShadowBox>

            {/* Action Buttons */}
            <BaseDrawer.Actions>
                <Button onClick={handleCancel} disabled={isBusy}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit(handleOnSubmit)}
                    disabled={isBusy}
                >
                    {isEditMode ? "Update" : "Save"} Charge
                </Button>
            </BaseDrawer.Actions>
        </LoadingWrapper>
    );
};

export default ServiceChargeForm;